import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { UserValidationService } from '../../services/user-validation.service';
import * as svg from './svg';

export interface NavigationLink {
  url: string;
  key: string;
  svg?: SafeHtml;
}

const sidebarHalf = 2;
const sidebarDebounce = 100;

@Component({
  selector: 'my-account-sidebar-main',
  templateUrl: './sidebar-main.component.html',
  styleUrls: ['./sidebar-main.component.scss']
})
export class SidebarMainComponent implements OnInit, AfterViewInit, OnDestroy {
  navigationLinks: NavigationLink[];

  scroll = false;

  isShowed = true;

  private list;

  private numOfMovements: number;

  private readonly ngUnsubscribe: Subject<any> = new Subject();

  @HostListener('window:resize', ['$event']) onResize(): void {
    if (this.list) {
      this.manageArrows();
    }
  }

  constructor(
    private readonly elemRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly router: Router,
    private readonly userValidationService: UserValidationService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  scrollIntoView(e: any): void {
    e.target.scrollIntoView({ inline: 'center', block: 'end' });
  }

  ngOnInit(): void {
    this.getNavigationLinks();
    this.userValidationService
      .manageSidebar()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: boolean) => {
        this.isShowed = data;
        this.changeDetectorRef.detectChanges();
      });
  }

  ngAfterViewInit(): void {
    this.list = this.elemRef.nativeElement.querySelector(
      '.sidebar-list-container'
    );
    this.numOfMovements =
      this.elemRef.nativeElement.querySelectorAll('.sidebar-item').length /
      sidebarHalf;

    this.createScrollEvent();
    this.moveInitialActiveUrl();
    setTimeout(() => this.manageArrows(), sidebarDebounce);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next('');
    this.ngUnsubscribe.complete();
  }

  moveToRight(): void {
    this.list.scrollLeft += Math.ceil(
      this.list.scrollWidth / this.numOfMovements
    );
  }

  moveToLeft(): void {
    this.list.scrollLeft =
      this.list.scrollLeft -
      Math.ceil(this.list.scrollWidth / this.numOfMovements);
  }

  private createScrollEvent() {
    fromEvent(this.list, 'scroll')
      .pipe(
        takeUntil(this.ngUnsubscribe),
        tap(() => (this.scroll = true)),
        debounceTime(sidebarDebounce)
      )
      .subscribe(() => {
        this.manageArrows();
        this.scroll = false;
      });
  }

  private moveInitialActiveUrl() {
    const activeIndex = this.navigationLinks.findIndex(
      item => item.url === this.router.url
    );
    const activeItem =
      this.elemRef.nativeElement.querySelectorAll('.sidebar-item')[activeIndex];
    if (activeItem) {
      activeItem.scrollIntoView({ inline: 'center', block: 'end' });
    }
  }

  private getNavigationLinks(): void {
    this.navigationLinks = [
      {
        url: '/',
        key: _('sidebar-main.home').toString(),
        svg: svg.svgs.home
      },
      {
        url: '/info',
        key: _('sidebar-main.info').toString(),
        svg: svg.svgs.data
      },
      {
        url: '/privacy',
        key: _('sidebar-main.privacy').toString(),
        svg: svg.svgs.privacy
      }
    ];
  }

  private manageArrows() {
    if (this.list.scrollLeft === 0) {
      this.renderer.addClass(
        this.elemRef.nativeElement.querySelector('.sidebar-arrow--left'),
        'none'
      );
    } else {
      this.renderer.removeClass(
        this.elemRef.nativeElement.querySelector('.sidebar-arrow--left'),
        'none'
      );
    }

    if (this.list.scrollWidth - this.list.scrollLeft <= this.list.clientWidth) {
      this.renderer.addClass(
        this.elemRef.nativeElement.querySelector('.sidebar-arrow--right'),
        'none'
      );
    } else {
      this.renderer.removeClass(
        this.elemRef.nativeElement.querySelector('.sidebar-arrow--right'),
        'none'
      );
    }
  }
}
